import { useEffect, useState, useCallback, useMemo } from "react";
import { Box, Flex, Heading, Text, Button, Stack, HStack, Icon, Badge, Spinner, Input } from "@chakra-ui/react";
import { HardHat, Plus, QrCode, FileDown, RefreshCw, Ban, Pencil, ClipboardList } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { cachePut, cacheGet, safeQuery, safeAll } from "@/lib/offline";
import { apiPost } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { SegmentedTabs } from "@/components/SegmentedTabs";
import { FeatureToggle } from "@/components/FeatureToggle";
import { showToast } from "@/components/AppToaster";
import { colors } from "@/theme/colors";

const inp = { h: "44px", bg: colors.white, borderColor: colors.border, borderRadius: "10px", fontSize: "sm" };

export default function Contractors() {
  const { user, activeCompanyId } = useAuth();
  const [tab, setTab] = useState("list");
  const [loading, setLoading] = useState(true);
  const [contractors, setContractors] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState({});
  const [tokens, setTokens] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [editing, setEditing] = useState(null); // null | {} | contractor row
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    const [csRes, tplRes, cfgRes, tokRes, locRes] = await safeAll([
      supabase.from("contractors").select("*").eq("company_id", activeCompanyId).order("name"),
      supabase.from("templates").select("id,title,type,company_id").eq("status", "published").order("title"),
      supabase.from("contractor_form_settings").select("*").eq("company_id", activeCompanyId),
      supabase.from("contractor_portal_tokens").select("*").eq("company_id", activeCompanyId).eq("active", true).order("created_at"),
      supabase.from("locations").select("id,name").eq("active", true)
        .eq("company_id", activeCompanyId).order("name"),
    ]);
    if (csRes.error || tplRes.error) {
      // Offline: serve the last-known-good snapshot instead of empty lists.
      const snap = cacheGet(user.id, "contractors") || {};
      setContractors(snap.contractors || []);
      setTemplates(snap.templates || []);
      setSettings(snap.settings || {});
      setTokens(snap.tokens || []);
      setLocations(snap.locations || []);
      setLoadFailed(true);
    } else {
      const tpls = (tplRes.data || []).filter((t) => t.company_id === activeCompanyId || t.company_id === null);
      const cfg = Object.fromEntries((cfgRes.data || []).map((r) => [r.template_id, r]));
      setContractors(csRes.data || []);
      setTemplates(tpls);
      setSettings(cfg);
      setTokens(tokRes.data || []);
      setLocations(locRes.data || []);
      setLoadFailed(false);
      cachePut(user.id, "contractors", {
        contractors: csRes.data || [], templates: tpls, settings: cfg,
        tokens: tokRes.data || [], locations: locRes.data || [],
      });
    }
    setLoading(false);
  }, [activeCompanyId, user.id]);
  useEffect(() => { load(); }, [load]);

  const toggleSetting = async (templateId, key) => {
    const cur = settings[templateId];
    const { error } = await supabase.from("contractor_form_settings").upsert({
      company_id: activeCompanyId, template_id: templateId,
      signoff_enabled: cur?.signoff_enabled || false,
      portal_enabled: cur?.portal_enabled || false,
      [key]: !(cur?.[key]),
    });
    if (error) { setErr(error.message); return; }
    load();
  };

  return (
    <Box data-testid="contractors-page" maxW="760px">
      <HStack gap="3" mb="1">
        <Icon as={HardHat} boxSize="6" color={colors.teal} />
        <Heading fontFamily="heading" fontWeight="700" fontSize={{ base: "2xl", md: "3xl" }} color={colors.textPrimary}>Contractors</Heading>
      </HStack>
      <Text color={colors.textSecondary} fontSize="sm" mb="5">
        Manage outside crews: your contractor list, which forms carry a sign-off box, and the no-login QR portal.
      </Text>

      <SegmentedTabs value={tab} onChange={setTab} tabs={[
        { key: "list", label: "Contractor list", testId: "tab-contractor-list" },
        { key: "forms", label: "Form settings", icon: ClipboardList, testId: "tab-form-settings" },
        { key: "portal", label: "QR portal", icon: QrCode, testId: "tab-qr-portal" },
      ]} />

      {err && <Box mt="4" px="4" py="2.5" borderRadius="10px" bg={colors.criticalBg} color={colors.criticalText} fontSize="sm" data-testid="contractors-error">{err}</Box>}

      {(() => {
        // step chain flattened for readability (was a nested ternary)
        if (loading) return <Flex minH="30vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>;
        if (tab === "list") return <ContractorList contractors={contractors} loadFailed={loadFailed} onAdd={() => setEditing({})} onEdit={setEditing} />;
        if (tab === "forms") return <FormSettings templates={templates} settings={settings} onToggle={toggleSetting} />;
        return <PortalTab tokens={tokens} locations={locations} companyId={activeCompanyId} reload={load} setErr={setErr} />;
      })()}

      {editing !== null && (
        <ContractorModal contractor={editing} companyId={activeCompanyId}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </Box>
  );
}

function ContractorList({ contractors, loadFailed, onAdd, onEdit }) {
  return (
    <Box mt="5">
      <Flex justify="space-between" align="center" mb="3">
        <Text fontSize="sm" color={colors.textSecondary}>{contractors.length} contractor{contractors.length === 1 ? "" : "s"}</Text>
        <Button size="sm" onClick={onAdd} data-testid="add-contractor-btn"><Plus size={15} /> Add contractor</Button>
      </Flex>
      {contractors.length === 0 ? (
        <Flex align="center" justify="center" minH="140px" borderRadius="14px" border="1px dashed" borderColor={colors.border} bg={colors.white} data-testid="contractors-empty">
          <Text color={colors.textSecondary} fontSize="sm">{loadFailed ? "You're offline — your contractor list will appear once this device has loaded it." : "No contractors yet — add your first outside crew."}</Text>
        </Flex>
      ) : (
        <Stack gap="2">
          {contractors.map((c) => (
            <Flex key={c.id} align="center" gap="3" bg={colors.white} border="1px solid" borderColor={colors.border}
              borderRadius="12px" px="4" py="3" opacity={c.status === "active" ? 1 : 0.55} data-testid={`contractor-row-${c.id}`}>
              <Icon as={HardHat} boxSize="5" color={c.status === "active" ? colors.teal : colors.textSecondary} flexShrink={0} />
              <Box flex="1" minW="0">
                <HStack gap="2">
                  <Text fontWeight="600" fontSize="sm" color={colors.textPrimary}>{c.name}</Text>
                  {c.trade && <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2" fontSize="10px">{c.trade}</Badge>}
                  {c.status === "inactive" && <Badge bg={colors.cream} color={colors.textSecondary} borderRadius="full" px="2" fontSize="10px">Inactive</Badge>}
                </HStack>
                <Text fontSize="xs" color={colors.textSecondary}>
                  {[c.contact_name, c.email, c.phone].filter(Boolean).join(" · ") || "No contact details"}
                </Text>
              </Box>
              <Button size="xs" variant="ghost" onClick={() => onEdit(c)} data-testid={`edit-contractor-${c.id}`} aria-label="Edit"><Pencil size={14} /></Button>
            </Flex>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function FormSettings({ templates, settings, onToggle }) {
  return (
    <Box mt="5">
      <Text fontSize="sm" color={colors.textSecondary} mb="3">
        <b>Sign-off box</b>: your workers can collect a contractor signature at the bottom of the form.{" "}
        <b>QR portal</b>: outside contractors can fill this form themselves — no login.
      </Text>
      <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" overflow="hidden" data-testid="form-settings-table">
        <Flex px="4" py="2.5" bg={colors.cream} gap="3" fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="0.05em" color={colors.textSecondary}>
          <Box flex="1">Form</Box>
          <Box w="92px" textAlign="center">Sign-off box</Box>
          <Box w="92px" textAlign="center">QR portal</Box>
        </Flex>
        {templates.map((t) => (
          <Flex key={t.id} px="4" py="3" gap="3" align="center" borderTop="1px solid" borderColor={colors.border} data-testid={`form-setting-row-${t.id}`}>
            <Box flex="1" minW="0">
              <Text fontSize="sm" fontWeight="500" color={colors.textPrimary}>{t.title}</Text>
              <Text fontSize="xs" color={colors.textSecondary}>{t.type}{t.company_id === null ? " · Hakina standard" : ""}</Text>
            </Box>
            <Flex w="92px" justify="center">
              <FeatureToggle on={Boolean(settings[t.id]?.signoff_enabled)} onChange={() => onToggle(t.id, "signoff_enabled")} testId={`toggle-signoff-${t.id}`} />
            </Flex>
            <Flex w="92px" justify="center">
              <FeatureToggle on={Boolean(settings[t.id]?.portal_enabled)} onChange={() => onToggle(t.id, "portal_enabled")} testId={`toggle-portal-${t.id}`} />
            </Flex>
          </Flex>
        ))}
      </Box>
    </Box>
  );
}

function PortalTab({ tokens, locations, companyId, reload, setErr }) {
  const [busy, setBusy] = useState(null);
  const [locPick, setLocPick] = useState("");
  const companyToken = useMemo(() => tokens.find((t) => !t.location_id), [tokens]);
  const locationTokens = useMemo(() => tokens.filter((t) => t.location_id), [tokens]);
  const unassignedLocations = useMemo(
    () => locations.filter((l) => !locationTokens.some((t) => t.location_id === l.id)),
    [locations, locationTokens]
  );

  const rotate = async (locationId) => {
    setBusy(locationId || "company");
    setErr(null);
    try {
      await apiPost("/contractors/portal-token", { company_id: companyId, location_id: locationId || null });
      showToast("QR code generated — any previous code for this scope is now invalid");
      reload();
    } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };

  const revoke = async (tokenId) => {
    if (!window.confirm("Revoke this QR code? Anyone scanning it will get a dead link.")) return;
    setBusy(tokenId);
    try {
      await apiPost("/contractors/portal-token/revoke", { token_id: tokenId });
      showToast("QR code revoked");
      reload();
    } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };

  const downloadPoster = async (tokenId) => {
    setBusy(`poster-${tokenId}`);
    try {
      const { data } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contractors/poster`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${data?.session?.access_token}` },
        body: JSON.stringify({ token_id: tokenId, base_url: window.location.origin }),
      });
      if (!res.ok) throw new Error("Could not build the poster");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "hakina-contractor-qr-poster.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };

  const QrCard = ({ tok, label }) => (
    <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="5" data-testid={`qr-card-${tok.id}`}>
      <Text fontWeight="600" fontSize="sm" color={colors.textPrimary} mb="3">{label}</Text>
      <Flex gap="5" align="center" flexWrap="wrap">
        <Box p="3" bg="white" border="1px solid" borderColor={colors.border} borderRadius="12px">
          <QRCodeSVG value={`${window.location.origin}/portal/${tok.token}`} size={150} data-testid={`qr-svg-${tok.id}`} />
        </Box>
        <Stack gap="2" flex="1" minW="220px">
          <Text fontSize="xs" color={colors.textSecondary} wordBreak="break-all" data-testid={`qr-url-${tok.id}`}>
            {window.location.origin}/portal/{tok.token}
          </Text>
          <HStack gap="2" flexWrap="wrap">
            <Button size="sm" variant="outline" onClick={() => downloadPoster(tok.id)} loading={busy === `poster-${tok.id}`} data-testid={`poster-btn-${tok.id}`}>
              <FileDown size={14} /> Poster PDF
            </Button>
            <Button size="sm" variant="outline" onClick={() => rotate(tok.location_id)} loading={busy === (tok.location_id || "company")} data-testid={`regenerate-btn-${tok.id}`}>
              <RefreshCw size={14} /> Regenerate
            </Button>
            <Button size="sm" variant="danger" onClick={() => revoke(tok.id)} loading={busy === tok.id} data-testid={`revoke-btn-${tok.id}`}>
              <Ban size={14} /> Revoke
            </Button>
          </HStack>
        </Stack>
      </Flex>
    </Box>
  );

  return (
    <Stack gap="4" mt="5">
      <Text fontSize="sm" color={colors.textSecondary}>
        Print the QR poster and display it on site (trailer, toolbox, gate). Contractors scan it and complete the
        forms you enabled in <b>Form settings</b> — no login, no sign-up. Regenerating or revoking kills the old link instantly.
      </Text>

      {companyToken ? (
        <QrCard tok={companyToken} label="Company QR — contractor picks the site" />
      ) : (
        <Flex align="center" justify="space-between" gap="3" bg={colors.white} border="1px dashed" borderColor={colors.border} borderRadius="14px" p="5" flexWrap="wrap">
          <Text fontSize="sm" color={colors.textSecondary}>No company QR code yet.</Text>
          <Button size="sm" onClick={() => rotate(null)} loading={busy === "company"} data-testid="generate-company-qr-btn">
            <QrCode size={15} /> Generate company QR
          </Button>
        </Flex>
      )}

      <Box>
        <Text fontWeight="600" fontSize="sm" color={colors.textPrimary} mb="2">Per-location QR (site pre-selected)</Text>
        <HStack gap="2" mb="3" flexWrap="wrap">
          <Box as="select" value={locPick} onChange={(e) => setLocPick(e.target.value)} data-testid="location-qr-select"
            px="3" h="40px" borderRadius="8px" border="1px solid" borderColor={colors.border} bg={colors.white} fontSize="sm" minW="200px">
            <option value="">Choose a location…</option>
            {unassignedLocations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </Box>
          <Button size="sm" variant="outline" disabled={!locPick} onClick={() => { rotate(locPick); setLocPick(""); }}
            loading={busy === locPick && !!locPick} data-testid="generate-location-qr-btn">
            <QrCode size={15} /> Generate
          </Button>
        </HStack>
        <Stack gap="3">
          {locationTokens.map((t) => (
            <QrCard key={t.id} tok={t} label={`Location QR — ${locations.find((l) => l.id === t.location_id)?.name || "site"}`} />
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}

function ContractorModal({ contractor, companyId, onClose, onSaved }) {
  const isNew = !contractor.id;
  const [f, setF] = useState({
    name: contractor.name || "", contact_name: contractor.contact_name || "",
    email: contractor.email || "", phone: contractor.phone || "",
    trade: contractor.trade || "", status: contractor.status || "active",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    if (!f.name.trim()) { setErr("Contractor name is required."); return; }
    setBusy(true);
    const row = { ...f, name: f.name.trim(), company_id: companyId, updated_at: new Date().toISOString() };
    const { error } = isNew
      ? await supabase.from("contractors").insert(row)
      : await supabase.from("contractors").update(row).eq("id", contractor.id);
    setBusy(false);
    if (error) { setErr(error.message); return; }
    showToast(isNew ? "Contractor added" : "Contractor updated");
    onSaved();
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <Modal open onClose={onClose} title={isNew ? "Add contractor" : "Edit contractor"} testId="contractor-modal">
      <Stack gap="3">
        {err && <Box px="3" py="2" borderRadius="8px" bg={colors.criticalBg} color={colors.criticalText} fontSize="sm" data-testid="contractor-modal-error">{err}</Box>}
        <Input {...inp} placeholder="Contractor / company name *" value={f.name} onChange={set("name")} data-testid="contractor-name-input" />
        <Input {...inp} placeholder="Contact person" value={f.contact_name} onChange={set("contact_name")} data-testid="contractor-contact-input" />
        <Input {...inp} type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="contractor-email-input" />
        <Input {...inp} type="tel" placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="contractor-phone-input" />
        <Input {...inp} placeholder="Trade / type (e.g. Electrical, Scaffolding)" value={f.trade} onChange={set("trade")} data-testid="contractor-trade-input" />
        <Box as="select" value={f.status} onChange={set("status")} data-testid="contractor-status-select"
          w="full" px="3" h="44px" borderRadius="10px" border="1px solid" borderColor={colors.border} bg={colors.white} fontSize="sm">
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </Box>
        <Button onClick={save} loading={busy} data-testid="contractor-save-btn">{isNew ? "Add contractor" : "Save changes"}</Button>
      </Stack>
    </Modal>
  );
}
